import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../utils/api";
import { useFeedback } from "../hooks/useFeedback";
import Spinner from "./ui/Spinner";
import CollapsibleCard from "./ui/CollapsibleCard";
import { usePipeline } from "../context/PipelineContext";

function Tooltip({ content, children }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const compute = () => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ top: r.top + r.height / 2, left: r.right + 10 });
  };

  useEffect(() => {
    if (!open) return;
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute, true);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute, true);
    };
  }, [open]);

  return (
    <>
      <span ref={ref} onMouseEnter={() => { compute(); setOpen(true); }} onMouseLeave={() => setOpen(false)}>
        {children}
      </span>
      {open && createPortal(
        <div
          className="fixed z-[99999] w-72 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700 shadow-lg"
          style={{ top: pos.top, left: pos.left, transform: "translateY(-50%)" }}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          {content}
          <div className="absolute left-[-5px] top-1/2 -translate-y-1/2 h-2.5 w-2.5 rotate-45 border-l border-b border-gray-200 bg-white" />
        </div>,
        document.body
      )}
    </>
  );
}

function IconInfo() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-gray-400">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
    </svg>
  );
}

function relativeTime(isoStr) {
  if (!isoStr) return null;
  const diff = Math.floor((Date.now() - new Date(isoStr)) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

function StepRow({ step, label, status, detail }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="mt-0.5 shrink-0">
        {status === "pending" && <span className="text-gray-300">○</span>}
        {status === "running" && <Spinner />}
        {status === "done"    && <span className="text-emerald-500">✓</span>}
        {status === "error"   && <span className="text-red-500">✗</span>}
      </span>
      <span className={status === "pending" ? "text-gray-400" : status === "error" ? "text-red-600" : "text-gray-700"}>
        <span className="font-medium">Étape {step} — </span>{label}
        {detail && <span className="text-gray-400 ml-1">({detail})</span>}
      </span>
    </div>
  );
}

const TOOLTIP_PIPELINE = (
  <div className="space-y-2">
    <p className="font-semibold">Pipeline complet</p>
    <p className="text-gray-500">Enchaîne automatiquement :</p>
    <ol className="list-decimal ml-4 space-y-1 text-gray-600">
      <li>Récupération Kizeo (formulaires concis + complets)</li>
      <li>Récupération Syrinix (événements bruts + fusion auto)</li>
      <li>Association v3 (ancre answer_time, fallback formulaire précédent)</li>
    </ol>
    <p className="text-gray-500">
      « Formulaires Kizeo seuls » saute l'étape Syrinix (plus rapide) : utile quand seuls de
      nouveaux formulaires sont attendus. L'association est relancée ensuite.
    </p>
  </div>
);

const TOOLTIP_RANGE = (
  <div className="space-y-2">
    <p className="font-semibold">Récupération par plage</p>
    <p className="text-gray-500">Télécharge Kizeo et Syrinix sur une fenêtre de dates précise.</p>
    <p className="text-gray-500">Ne lance <strong>pas</strong> l'association — à déclencher manuellement ensuite.</p>
    <p className="text-gray-500">Utile pour rattraper une période manquée sans écraser l'état de sync.</p>
  </div>
);

function PlanificationAuto() {
  const [open, setOpen]     = useState(false);
  const [cfg, setCfg]       = useState(null);
  const [hour, setHour]     = useState("02");
  const [minute, setMinute] = useState("00");
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [feedback, showFeedback] = useFeedback();

  useEffect(() => {
    if (!open || cfg !== null) return;
    apiFetch("/api/sync/schedule")
      .then(d => {
        setCfg(d);
        setEnabled(!!d.enabled);
        setHour(String(d.hour ?? 2).padStart(2, "0"));
        setMinute(String(d.minute ?? 0).padStart(2, "0"));
      })
      .catch(() => setCfg({}));
  }, [open, cfg]);

  const save = async () => {
    setSaving(true);
    const h = Number(hour);
    const m = Number(minute);
    if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || h > 23 || m < 0 || m > 59) {
      showFeedback("error", "Heure invalide.");
      setSaving(false);
      return;
    }
    try {
      const d = await apiFetch(
        `/api/sync/schedule?enabled=${enabled}&hour=${h}&minute=${m}`,
        { method: "POST" }
      );
      setCfg(d);
      const label = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      showFeedback("success", enabled ? `Ingest planifié à ${label} chaque jour.` : "Planification désactivée.");
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-gray-100">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <span className="flex items-center gap-1.5">
          🕐 Planification automatique
          {cfg?.enabled && (
            <span className="badge badge-active ml-1">
              {String(cfg.hour ?? 2).padStart(2, "0")}:{String(cfg.minute ?? 0).padStart(2, "0")} quotidien
            </span>
          )}
        </span>
        <span>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 flex flex-col gap-3">
          {cfg === null ? (
            <p className="text-xs text-gray-400">Chargement…</p>
          ) : (
            <>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={e => setEnabled(e.target.checked)}
                  className="w-4 h-4 accent-violet-600"
                />
                Activer l'ingest quotidien automatique
              </label>

              {enabled && (
                <>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 w-12">Heure</label>
                    <input
                      type="number" min="0" max="23" value={hour}
                      onChange={e => setHour(e.target.value.padStart(2, "0"))}
                      className="input w-16 text-center"
                    />
                    <span className="text-gray-400">:</span>
                    <input
                      type="number" min="0" max="59" value={minute}
                      onChange={e => setMinute(e.target.value.padStart(2, "0"))}
                      className="input w-16 text-center"
                    />
                    <span className="text-xs text-gray-400">(heure Paris)</span>
                  </div>
                </>
              )}

              {feedback && (
                <div className={`feedback-${feedback.type === "success" ? "success" : "error"}`}>
                  {feedback.msg}
                </div>
              )}

              <button onClick={save} disabled={saving} className="btn btn-sm btn-primary self-start">
                {saving ? <><Spinner /> Enregistrement…</> : "Enregistrer"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function RecuperationDesDonnees() {
  const {
    mode, setMode,
    expanded, setExpanded,
    running, setRunning,
    steps, setSteps,
    result, setResult,
    rangeFrom, setRangeFrom,
    rangeTo, setRangeTo,
    range, setRange,
    lastSync, setLastSync,
  } = usePipeline();

  // Statut serveur — poll permanent (4s) pour détecter tout ingest en cours
  const [serverStatus, setServerStatus] = useState(null);
  const bgPollRef = useRef(null);

  // Intervalles des polls actifs (ingest étape 1, association étape 2, cf. runPipeline) —
  // contrairement à bgPollRef ci-dessus, ceux-là sont créés à l'intérieur d'une fonction
  // déclenchée par clic, pas d'un effect, donc pas nettoyés automatiquement si le composant
  // démonte pendant l'exécution (utilisateur change d'onglet) : polling infini en tâche de
  // fond sinon (audit sécu/qualité P1-R, 04/09/2026). Suivis ici pour un nettoyage groupé au
  // démontage.
  const activeIntervalsRef = useRef(new Set());
  useEffect(() => {
    return () => {
      activeIntervalsRef.current.forEach(clearInterval);
      activeIntervalsRef.current.clear();
    };
  }, []);

  const fetchStatus = async () => {
    try {
      const st = await apiFetch("/api/sync/status");
      setServerStatus(st);
      setLastSync(st);
      return st;
    } catch { return null; }
  };

  useEffect(() => {
    fetchStatus();
    bgPollRef.current = setInterval(fetchStatus, 4000);
    return () => clearInterval(bgPollRef.current);
  }, []);

  // fetchMode : "incremental" | "full" | "kizeo" (formulaires Kizeo seuls, sans Syrinix —
  // P1-M, 27/10/2026). Les trois enchaînent l'association v3 ensuite.
  const runPipeline = async (fetchMode) => {
    const kizeoOnly = fetchMode === "kizeo";
    setExpanded(false);
    setRunning(true);
    setResult(null);
    setSteps([
      { step: 1, label: kizeoOnly ? "Récupération Kizeo" : "Récupération Kizeo + Syrinix", status: "pending", detail: null },
      { step: 2, label: "Association v3", status: "pending", detail: null },
    ]);

    // Lancer la récupération en background
    const ingestUrl = kizeoOnly
      ? "/api/sync/kizeo"
      : `/api/sync/ingest${fetchMode === "full" ? "?force_full=true" : ""}`;
    try {
      await apiFetch(ingestUrl, { method: "POST" });
    } catch (e) {
      setSteps(prev => prev.map(s => s.step === 1 ? { ...s, status: "error", detail: e.message } : s));
      setRunning(false);
      return;
    }

    setSteps(prev => prev.map(s => s.step === 1 ? { ...s, status: "running" } : s));

    // Poll actif (2s) jusqu'à la fin de l'ingest
    let finalSt = null;
    await new Promise(resolve => {
      const id = setInterval(async () => {
        const st = await apiFetch("/api/sync/status").catch(() => null);
        if (!st) return;
        setServerStatus(st);
        setLastSync(st);
        const parts = [];
        if (st.running_step_label) parts.push(st.running_step_label);
        if (st.running_kizeo_label)   parts.push(`Kizeo : ${st.running_kizeo_label}`);
        if (st.running_syrinix_label) parts.push(`Syrinix : ${st.running_syrinix_label}`);
        setSteps(prev => prev.map(s => s.step === 1 ? { ...s, detail: parts.join("  ·  ") || null } : s));
        if (!st.running) {
          clearInterval(id);
          activeIntervalsRef.current.delete(id);
          finalSt = st;
          resolve();
        }
      }, 2000);
      activeIntervalsRef.current.add(id);
    });

    // Kizeo seul : des erreurs ponctuelles (un formulaire) n'empêchent pas l'association, que le
    // serveur a déjà lancée — signalées à part (last_warning) sans arrêter le pipeline.
    if (finalSt?.last_error) {
      setSteps(prev => prev.map(s =>
        s.step === 1 ? { ...s, status: "error", detail: finalSt.last_error } : s
      ));
      setRunning(false);
      return;
    }

    setSteps(prev => prev.map(s =>
      s.step === 1 ? { ...s, status: "done", detail: finalSt?.last_warning ? `⚠️ ${finalSt.last_warning}` : null } :
      s.step === 2 ? { ...s, status: "running" } : s
    ));

    // Association v3 — tâche de fond depuis le 21/07/2026 (associate_for_site_v4 fait de vrais
    // appels Syrinix, potentiellement long : on ne peut plus attendre une réponse synchrone),
    // même modèle start + poll que l'ingest ci-dessus.
    try {
      await apiFetch("/api/match/sort-and-associate-v3", { method: "POST" });
    } catch (e) {
      setSteps(prev => prev.map(s => s.step === 2 ? { ...s, status: "error", detail: e.message } : s));
      setRunning(false);
      return;
    }

    let assocData = null;
    await new Promise(resolve => {
      const id = setInterval(async () => {
        const st = await apiFetch("/api/match/sort-and-associate-v3/status").catch(() => null);
        if (!st) return;
        const parts = [];
        if (st.current_site) parts.push(`Site ${st.current_site}${st.current_site_step ? ` (${st.current_site_step})` : ""}`);
        if (st.sites_total) parts.push(`${st.sites_done}/${st.sites_total} sites`);
        setSteps(prev => prev.map(s => s.step === 2 ? { ...s, detail: parts.join("  ·  ") || null } : s));
        if (!st.running) {
          clearInterval(id);
          activeIntervalsRef.current.delete(id);
          assocData = st;
          resolve();
        }
      }, 2000);
      activeIntervalsRef.current.add(id);
    });

    if (assocData?.last_error) {
      setSteps(prev => prev.map(s =>
        s.step === 2 ? { ...s, status: "error", detail: assocData.last_error } : s
      ));
      setRunning(false);
      fetchStatus();
      return;
    }

    const result = assocData?.result ?? {};
    const matched = result.matches_total ?? 0;
    const failedSites = result.sites_failed ?? [];
    let detail = `${matched} formulaire${matched !== 1 ? "s" : ""} associé${matched !== 1 ? "s" : ""}`;
    if (failedSites.length > 0) {
      detail += ` — ⚠️ ${failedSites.length} site${failedSites.length !== 1 ? "s" : ""} en échec (${failedSites.map(s => s.site_id).join(", ")}), non réassociés cette fois`;
    }
    setSteps(prev => prev.map(s =>
      s.step === 2 ? { ...s, status: "done", detail } : s
    ));
    setResult({ assoc: result });
    setRunning(false);
    fetchStatus();
  };

  const runRange = async () => {
    if (!rangeFrom || !rangeTo) return;
    setRange({ status: "running", result: null, error: null });
    try {
      const data = await apiFetch(
        `/api/sync/ingest-range?date_from=${rangeFrom}&date_to=${rangeTo}`,
        { method: "POST" }
      );
      setRange({ status: "done", result: data, error: null });
    } catch (e) {
      setRange({ status: "error", result: null, error: e.message });
    }
  };

  // Dernière récupération Kizeo : la plus récente entre un ingest complet et un "Kizeo seul".
  const lastKizeoRun = [lastSync?.last_run_iso, lastSync?.kizeo_last_run_iso]
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a))[0];

  // Bannière de statut : visible dans le header quand un ingest tourne
  const headerRight = serverStatus?.running ? (
    <span className="flex items-center gap-1.5 text-xs text-violet-600 font-medium">
      <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse shrink-0" />
      En cours…
    </span>
  ) : lastSync?.last_run_iso ? (
    <span className="text-xs text-gray-400">
      Dernier sync : {relativeTime(lastSync.last_run_iso)}
      {lastSync.last_run_mode && (
        <span className="ml-1 badge badge-neutral">{lastSync.last_run_mode}</span>
      )}
    </span>
  ) : null;

  return (
    <CollapsibleCard
      icon="🔧"
      title="Récupération des données"
      headerRight={headerRight}
    >
      {/* Bannière passive : ingest en cours mais pas lancé depuis cette session */}
      {serverStatus?.running && !running && (
        <div className="flex items-center gap-2 px-4 py-2 bg-violet-50 border-b border-violet-100 text-xs text-violet-700">
          <Spinner />
          <span className="font-medium">
            {serverStatus.running_mode === "kizeo" ? "Récupération Kizeo en cours" : "Ingest en cours"}
          </span>
          {serverStatus.running_step_label && (
            <span className="text-violet-500">— {serverStatus.running_step_label}</span>
          )}
          {serverStatus.running_kizeo_label && (
            <span className="text-violet-500">Kizeo : {serverStatus.running_kizeo_label}</span>
          )}
          {serverStatus.running_syrinix_label && (
            <span className="text-violet-500">Syrinix : {serverStatus.running_syrinix_label}</span>
          )}
          {serverStatus.running_since && (
            <span className="ml-auto text-violet-400">{relativeTime(serverStatus.running_since)}</span>
          )}
        </div>
      )}

      <div className="flex border-b border-gray-100">
        {[
          { id: "pipeline", label: "Récupérer + Associer" },
          { id: "range",    label: "Sur une période" },
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => { setMode(id); setExpanded(false); }}
            className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
              mode === id
                ? "border-violet-600 text-violet-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="p-4 flex flex-col gap-3">
        {mode === "pipeline" && (
          <>
            {!expanded && !running && !steps && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setExpanded(true)}
                  disabled={serverStatus?.running}
                  className="btn btn-md btn-primary flex-1"
                >
                  {serverStatus?.running ? <><Spinner /> Ingest en cours…</> : "Lancer le pipeline"}
                </button>
                <Tooltip content={TOOLTIP_PIPELINE}>
                  <span className="btn btn-icon btn-ghost cursor-default"><IconInfo /></span>
                </Tooltip>
              </div>
            )}

            {expanded && (
              <div className="flex flex-col gap-2">
                <p className="text-xs text-gray-500">Choisissez le mode de récupération :</p>
                <button onClick={() => runPipeline("incremental")} className="btn btn-md btn-primary">
                  Depuis le dernier sync
                  {lastSync?.last_run_iso && (
                    <span className="ml-1.5 text-violet-200 text-xs font-normal">
                      ({relativeTime(lastSync.last_run_iso)})
                    </span>
                  )}
                </button>
                <button onClick={() => runPipeline("kizeo")} className="btn btn-md btn-secondary">
                  Formulaires Kizeo seuls
                  {lastKizeoRun && (
                    <span className="ml-1.5 text-gray-400 text-xs font-normal">
                      ({relativeTime(lastKizeoRun)})
                    </span>
                  )}
                </button>
                <button onClick={() => runPipeline("full")} className="btn btn-md btn-danger">
                  Tout récupérer à nouveau ⚠️
                </button>
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                  "Tout récupérer" ignore l'historique de sync et recharge l'intégralité des données. Les données existantes seront écrasées.
                </p>
                <button onClick={() => setExpanded(false)} className="btn btn-sm btn-ghost self-start">
                  Annuler
                </button>
              </div>
            )}

            {!running && steps && (
              <button
                onClick={() => { setSteps(null); setResult(null); setExpanded(false); }}
                className="btn btn-sm btn-secondary self-start"
              >
                ← Nouveau pipeline
              </button>
            )}

            {steps && (
              <div className="flex flex-col gap-2 py-1">
                {steps.map(s => <StepRow key={s.step} {...s} />)}
              </div>
            )}

            {result?.assoc && (
              <div className="text-xs text-gray-500 pt-1 border-t border-gray-100">
                Association v3 : {result.assoc.matches_total ?? 0} associé{(result.assoc.matches_total ?? 0) !== 1 ? "s" : ""},
                {" "}{result.assoc.no_matches ?? 0} non associé{(result.assoc.no_matches ?? 0) !== 1 ? "s" : ""},
                {" "}{result.assoc.sorted?.mee ?? 0} MEE
              </div>
            )}
          </>
        )}

        {mode === "range" && (
          <>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex flex-col gap-1 flex-1 min-w-[120px]">
                  <label className="text-xs text-gray-500">Du</label>
                  <input type="date" value={rangeFrom} onChange={e => setRangeFrom(e.target.value)}
                    disabled={range.status === "running"} className="input" />
                </div>
                <div className="flex flex-col gap-1 flex-1 min-w-[120px]">
                  <label className="text-xs text-gray-500">Au</label>
                  <input type="date" value={rangeTo} onChange={e => setRangeTo(e.target.value)}
                    disabled={range.status === "running"} className="input" />
                </div>
                <Tooltip content={TOOLTIP_RANGE}>
                  <span className="btn btn-icon btn-ghost cursor-default mb-0.5"><IconInfo /></span>
                </Tooltip>
              </div>
              <button
                onClick={runRange}
                disabled={range.status === "running" || !rangeFrom || !rangeTo}
                className="btn btn-md btn-primary"
              >
                {range.status === "running" ? <><Spinner /> Téléchargement…</> : "Télécharger"}
              </button>
            </div>

            {range.error && <div className="feedback-error">{range.error}</div>}

            {range.result && (
              <div className="flex flex-col gap-2 pt-1 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-600">
                  Période : {range.result.range?.from} → {range.result.range?.to}
                </p>
                {(range.result.syrinix || []).map(s => (
                  <div key={s.site} className={`rounded-lg border px-3 py-2 text-xs ${s.syrinix_error ? "border-red-200 bg-red-50" : "border-gray-100 bg-gray-50"}`}>
                    <div className="font-mono text-gray-500 mb-1">{s.site}</div>
                    {s.syrinix_error ? (
                      <span className="text-red-600">{s.syrinix_error}</span>
                    ) : (
                      <div className="flex gap-3">
                        <span className="text-emerald-700 font-medium">{s.new_events ?? 0} événement{s.new_events !== 1 ? "s" : ""}</span>
                        {s.merge?.merged != null && <span className="text-gray-500">→ {s.merge.merged} fusionné{s.merge.merged !== 1 ? "s" : ""}</span>}
                      </div>
                    )}
                  </div>
                ))}
                <div className="text-xs text-gray-500">
                  Kizeo : {range.result.totals?.new_kizeo_entries ?? 0} nouveau{(range.result.totals?.new_kizeo_entries ?? 0) !== 1 ? "x" : ""} formulaire{(range.result.totals?.new_kizeo_entries ?? 0) !== 1 ? "s" : ""}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <PlanificationAuto />
    </CollapsibleCard>
  );
}
